import React, { useCallback, createContext, forwardRef } from "react";
import { FixedSizeGrid as Grid } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { Skeleton, Tooltip, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getMonthName } from "../lib";

function setColor(red, green, blue, trans) {
  var transFinal = trans ? trans : "1";
  return `rgba(${red},${green},${blue},${transFinal})`;
}

function InfiniteScrollWrapper({
  // Array of items loaded so far.
  items,
  days,
  isLoading,
  month,
  year,
}) {
  // If there are more items to be loaded then add an extra row to hold a loading indicator.
  const itemCount = items.length;

  const buttonStyles = useCallback((red, green, blue, antrag) => {
    var tmpstyle = null;
    if (antrag === "J") {
      tmpstyle = {
        background:
          "repeating-linear-gradient(-53deg,  " +
          setColor(red, green, blue, "0") +
          ",  " +
          setColor(red, green, blue, "0") +
          " 5px,  " +
          setColor(red, green, blue, "0.7") +
          " 5px,  " +
          setColor(red, green, blue, "0.7") +
          " 10px)",
        height: "25px",
        width: "25px",
        flexGrow: 1,
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
      };
    } else {
      tmpstyle = {
        backgroundColor: `rgba(${red},${green},${blue},1)`,
        color: (0.299 * Number(red) + 0.587 * Number(green) + 0.114 * Number(blue)) / 255 > 0.6 ? "#000000" : "#ffffff",
        height: "25px",
        width: "25px",
        flexGrow: 1,
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
      };
    }
    return tmpstyle;
  }, []);

  // Render an item or a loading indicator.
  const Item = useCallback(
    ({ columnIndex, rowIndex, style }) => {
      const dataObject = items[rowIndex];
      const dayObject = dataObject?.days[columnIndex] ?? {};
      const daystyle = {
        width: "50px",
        height: "100%",
        // borderRight:  "1px solid #2299ff22",

        alignItems: "center",
        justifyContent: "center",
        backgroundColor:
          (Number(month) === new Date().getMonth() && columnIndex + 1 === new Date().getDate() && Number(year) === new Date().getFullYear()) ||
          (Number(month) + 1 === new Date().getMonth() &&
            Number(days[columnIndex].date ?? 0) === new Date().getDate() &&
            Number(days[columnIndex].date ?? 0) < columnIndex &&
            Number(year) === new Date().getFullYear()) ||
          (Number(month) === 11 &&
            0 === new Date().getMonth() &&
            Number(days[columnIndex].date ?? 0) === new Date().getDate() &&
            Number(days[columnIndex].date ?? 0) < columnIndex &&
            Number(year) + 1 === new Date().getFullYear())
            ? rowIndex % 2 !== 0
              ? "#ff555555"
              : "#ff555566"
            : days[columnIndex].style === "weekend"
            ? rowIndex % 2 !== 0
              ? "#88aacc44"
              : "#88aacc55"
            : rowIndex % 2 !== 0
            ? "#ffffff00"
            : "#88aacc22",
      };
      if (isLoading)
        return (
          <div style={style}>
            <div style={daystyle}>
              <div style={{ height: "25px" }}>{days[columnIndex].date}</div>
            </div>
          </div>
        );

      return (
        <div style={style}>
          <div style={daystyle}>
            <div style={{ height: "25px" }}>{days[columnIndex].date}</div>
            <div
              style={{
                height: "25px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-evenly",
              }}
            >
              {dayObject.fz?.map((fzObject) => (
                <Tooltip title={fzObject.tip}>
                  <div style={buttonStyles(fzObject.red, fzObject.green, fzObject.blue, fzObject.antrag)}>
                    <Typography>{fzObject.text}</Typography>
                  </div>
                </Tooltip>
              ))}
            </div>
          </div>
        </div>
      );
    },
    [items, isLoading, buttonStyles, days]
  );

  return (
    <AutoSizer>
      {({ height, width }) => {
        // Use these actual sizes to calculate your percentage based sizes
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              height: height,
              width: width,
            }}
          >
            <StickyGrid
              height={height}
              width={width}
              columnCount={days.length}
              rowCount={isLoading ? 2 : itemCount}
              rowHeight={50}
              columnWidth={50}
              stickyHeight={100}
              stickyWidth={150}
              items={isLoading ? [] : items}
              days={days}
              isLoading={isLoading}
              month={month}
              year={year}
            >
              {Item}
            </StickyGrid>
          </div>
        );
      }}
    </AutoSizer>
  );
}

const getRenderedCursor = (children) =>
  children.reduce(
    ([minRow, maxRow, minColumn, maxColumn], { props: { columnIndex, rowIndex } }) => {
      if (rowIndex < minRow) {
        minRow = rowIndex;
      }
      if (rowIndex > maxRow) {
        maxRow = rowIndex;
      }
      if (columnIndex < minColumn) {
        minColumn = columnIndex;
      }
      if (columnIndex > maxColumn) {
        maxColumn = columnIndex;
      }

      return [minRow, maxRow, minColumn, maxColumn];
    },
    [Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY]
  );

const headerBuilder = (minColumn, maxColumn, columnWidth, stickyHeight) => {
  const columns = [];

  for (let i = minColumn; i <= maxColumn; i++) {
    columns.push({
      height: stickyHeight,
      width: columnWidth,
      left: i * columnWidth,
      label: `Col ${i}`,
      index: i,
    });
  }

  return columns;
};

const columnsBuilder = (minRow, maxRow, rowHeight, stickyWidth) => {
  const rows = [];

  for (let i = minRow; i <= maxRow; i++) {
    rows.push({
      height: rowHeight,
      width: stickyWidth,
      top: i * rowHeight,
      label: `Row ${i}`,
      key: i,
    });
  }

  return rows;
};

const StickyHeader = ({ stickyHeight, stickyWidth, headerColumns, days, month, year }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const baseStyle = {
    height: stickyHeight,
    width: stickyWidth,
    zIndex: 3,
    position: "sticky",
    left: 0,
    paddingLeft: "10px",
    backgroundColor: theme.palette.background.default,
  };
  const headerStyle = {
    position: "sticky",
    top: 0,
    left: 0,
    display: "flex",
    flexDirection: "row",
    backgroundColor: theme.palette.background.default,
    zIndex: 3,
  };
  const scrollableStyle = {
    left: stickyWidth,
    top: "50px",
    height: "50px",
    position: "absolute",
  };

  var firstMonthWidth = 0;
  days.forEach((element, index) => {
    if (index < Number(element.date)) firstMonthWidth += 50;
  });

  return (
    <>
      <div style={headerStyle}>
        <div style={baseStyle}>
          <div
            style={{
              height: "50px",
              width: "150px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            Monat
          </div>
          <div
            style={{
              height: "50px",
              width: "150px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            Personen
          </div>
        </div>
        <div
          style={{
            width: String(firstMonthWidth + 140) + "px",
            position: "absolute",
            height: "50px",
            left: "0px",
            top: "0px",
            display: "flex",
          }}
        >
          <div
            style={{
              position: "sticky",
              left: "150px",
              display: "flex",
              justifyItems: "center",
              alignItems: "center",
            }}
          >
            <Typography>{t(getMonthName(month))}</Typography>
          </div>
          <div className="w-12"></div>
        </div>
        <div
          style={{
            width: "1500px",
            position: "absolute",
            height: "50px",
            left: String(firstMonthWidth + 160) + "px",
            top: "0px",
            display: "flex",
          }}
        >
          <div
            style={{
              position: "sticky",
              left: "150px",
              display: "flex",
              justifyItems: "center",
              alignItems: "center",
            }}
          >
            <Typography>{t(getMonthName(month + 1 !== 12 ? month + 1 : 0))}</Typography>
          </div>
          <div className="w-12"></div>
        </div>
        <div>
          <div style={scrollableStyle}>
            {headerColumns.map(({ label, index }, i) => {
              const newStyle = {
                position: "absolute",
                backgroundColor:
                  (Number(month) === new Date().getMonth() && index + 1 === new Date().getDate() && Number(year) === new Date().getFullYear()) ||
                  (Number(month) + 1 === new Date().getMonth() &&
                    Number(days[index].date) === new Date().getDate() &&
                    Number(days[index].date) < index &&
                    Number(year) === new Date().getFullYear()) ||
                  (Number(month) === 11 &&
                    0 === new Date().getMonth() &&
                    Number(days[index].date ?? 0) === new Date().getDate() &&
                    Number(days[index].date ?? 0) < index &&
                    Number(year) + 1 === new Date().getFullYear())
                    ? "#ff333355"
                    : days[index].style === "weekend"
                    ? "#88aacc44"
                    : "#ffffff00",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                height: "50px",
                width: "50px",
                left: String(index * 50) + "px",
              };
              return (
                <div style={newStyle} key={i}>
                  {days[index].key}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

const StickyColumns = ({ rows, stickyHeight, stickyWidth, items, isLoading }) => {
  const theme = useTheme();
  const leftSideStyle = {
    top: stickyHeight,
    width: stickyWidth,
    height: `calc(100% - ${stickyHeight}px)`,
    position: "sticky",
    left: 0,
    zIndex: 2,
    background: theme.palette.background.default,
  };

  if (isLoading) {
    return (
      <div style={leftSideStyle}>
        <div
          style={{
            alignItems: "center",
            justifyContent: "stretch",
            position: "absolute",
            display: "flex",
            flexDirection: "row",
            paddingLeft: "10px",
            backgroundColor: "#88aacc22",
            top: "0px",
            height: "50px",
            width: "150px",
          }}
        >
          <Skeleton width={130}></Skeleton>
        </div>
        <div
          style={{
            alignItems: "center",
            justifyContent: "stretch",
            position: "absolute",
            display: "flex",
            flexDirection: "row",
            paddingLeft: "10px",
            backgroundColor: "#ffffff00",
            top: "50px",
            height: "50px",
            width: "150px",
          }}
        >
          <Skeleton width={130}></Skeleton>
        </div>
      </div>
    );
  }
  return (
    <div style={leftSideStyle}>
      {rows.map((element, i) => {
        const newStyle = {
          alignItems: "left",
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          paddingLeft: "10px",
          backgroundColor: element.key % 2 !== 0 ? "#ffffff00" : "#88aacc22",
          top: String(element.key * 50) + "px",
          height: "50px",
          width: "150px",
        };
        return (
          <div style={newStyle} key={String(i)}>
            <div style={{height: "24px", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis"}}>
              <Typography style={{height: "24px", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis"}} align="left">{items[element.key].name ?? ""}</Typography>
            </div>
            <div>
              <Typography color="slategray" align="left">{items[element.key].vkey ?? ""}</Typography>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const StickyGridContext = createContext();
StickyGridContext.displayName = "StickyGridContext";

const innerGridElementType = forwardRef(({ children, ...rest }, ref) => (
  <StickyGridContext.Consumer>
    {({ stickyHeight, stickyWidth, headerBuilder, columnsBuilder, columnWidth, rowHeight, items, days, isLoading, month, year }) => {
      const [minRow, maxRow, minColumn, maxColumn] = getRenderedCursor(children); // TODO maybe there is more elegant way to get this
      const headerColumns = headerBuilder(minColumn, maxColumn, columnWidth, stickyHeight);
      const leftSideRows = columnsBuilder(minRow, maxRow, rowHeight, stickyWidth);
      const containerStyle = {
        ...rest.style,
        width: `${parseFloat(rest.style.width) + stickyWidth}px`,
        height: `${parseFloat(rest.style.height) + stickyHeight}px`,
      };
      const containerProps = { ...rest, style: containerStyle };
      const gridDataContainerStyle = {
        top: stickyHeight,
        left: stickyWidth,
        position: "absolute",
      };

      return (
        <div ref={ref} {...containerProps}>
          <StickyHeader
            headerColumns={headerColumns}
            stickyHeight={stickyHeight}
            stickyWidth={stickyWidth}
            days={days}
            isLoading={isLoading}
            month={month}
            year={year}
          />
          <StickyColumns rows={leftSideRows} stickyHeight={stickyHeight} stickyWidth={stickyWidth} items={items} isLoading={isLoading} />

          <div style={gridDataContainerStyle}>{children}</div>
        </div>
      );
    }}
  </StickyGridContext.Consumer>
));

const StickyGrid = ({ stickyHeight, stickyWidth, columnWidth, rowHeight, children, items, days, isLoading, month, year, ...rest }) => (
  <StickyGridContext.Provider
    value={{
      stickyHeight,
      stickyWidth,
      columnWidth,
      rowHeight,
      headerBuilder,
      columnsBuilder,
      items,
      days,
      isLoading,
      month,
      year,
    }}
  >
    <Grid columnWidth={columnWidth} rowHeight={rowHeight} innerElementType={innerGridElementType} {...rest}>
      {children}
    </Grid>
  </StickyGridContext.Provider>
);

export default InfiniteScrollWrapper;
